import { useListEvents } from '@cocoonspace/sdk-js/domains/events/events'
import { useEventExtrasList } from '@cocoonspace/shared/hooks/cocoon-api'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { useMemo } from 'react'

export const useFeaturedEvents = () => {
	const { data: event } = useListEvents(
		{ include: 'current', limit: 1 },
		{
			query: {
				select: ({ data }) => {
					if (!data?.length) return undefined

					const event = data[0]
					const startDate = dateTimeUtils(event.start_local)
					const isToday = dateTimeUtils().diff(startDate, 'week') === 0

					return isToday ? event : undefined
				},
			},
		},
	)

	const { data: eventExtras } = useEventExtrasList(event?.id || '', {
		option: true,
	})

	const daysBeforeEvent = useMemo(() => {
		const eventDate = dateTimeUtils(event?.start_local)

		return Math.ceil(eventDate.diff(dateTimeUtils(), 'day', true))
	}, [event])

	const daysBeforeEventLabel = useMemo(() => {
		const eventDate = dateTimeUtils(event?.start_local)
		const eventStart = dateTimeUtils(event?.start_local)
		const eventEnd = dateTimeUtils(event?.end_local)

		if (dateTimeUtils().isBetween(eventStart, eventEnd)) {
			return 'common:dateTimes.relativeTimes.ongoing'
		} else if (eventDate.isTomorrow()) {
			return 'common:dateTimes.relativeTimes.tomorrow'
		} else if (eventDate.isToday()) {
			return 'common:dateTimes.relativeTimes.today'
		} else if (daysBeforeEvent === 7) {
			return 'common:dateTimes.relativeTimes.nextWeek'
		} else {
			return 'common:dateTimes.relativeTimes.nDays'
		}
	}, [event?.end_local, event?.start_local, daysBeforeEvent])

	const emptyMenusQty = useMemo(() => {
		if (!eventExtras?.length) return 0

		const extrasWithNeededChoice = eventExtras
			// TODO: `needs_choice` is available in the `/event/{id}/extra` endpoint response, not in `/event/{id}`
			// @ts-ignore
			.filter((extra) => extra.needs_choice)

		const diffMenus = extrasWithNeededChoice.reduce(
			(acc, extra) => {
				const choicesQty = extra.choices?.reduce(
					(total, choice) => total + choice.quantity,
					0,
				)

				return {
					totalMenus: acc.totalMenus + extra.quantity,
					totalChoices: acc.totalChoices + (choicesQty || 0),
				}
			},
			{ totalMenus: 0, totalChoices: 0 },
		)

		return diffMenus.totalMenus - diffMenus.totalChoices
	}, [eventExtras])

	const isExtrasUpdatable = useMemo(() => {
		const extrasDate = dateTimeUtils(event?.extras_allowed_until)

		return dateTimeUtils().isBefore(extrasDate)
	}, [event])

	const extrasExpirationDate = useMemo(
		// TODO: remplacer par l'util de date format -> pas i18n friendly
		() =>
			dateTimeUtils(event?.extras_allowed_until).format('D MMMM [à] H[h]mm'),
		[event],
	)

	return {
		event,
		daysBeforeEvent,
		daysBeforeEventLabel,
		emptyMenusQty,
		isExtrasUpdatable,
		extrasExpirationDate,
	}
}
